import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Save, Download, Mail, RefreshCw, Plus, Trash2, History as HistoryIcon, Package } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { authenticatedDownload } from "../lib/pdf";
import USDateInput from "../components/USDateInput";
import DashboardShell from "../components/shell/DashboardShell";
import PfsHistoryDialog from "../components/PfsHistoryDialog";
import PfsNotificationsBell from "../components/PfsNotificationsBell";
import PfsShareDialog from "../components/PfsShareDialog";
import { useAuth } from "../context/AuthContext";
import {
  fmtUSD,
  num,
  MoneyInput,
  KPI,
  Card,
  Row,
  Field,
} from "../components/pfs/PfsPrimitives";
import LiabilityScheduleCard from "../components/pfs/LiabilityScheduleCard";
import DescribedScheduleCard from "../components/pfs/DescribedScheduleCard";
import EmailToLenderDialog from "../components/pfs/EmailToLenderDialog";
import LenderPackageDialog from "../components/pfs/LenderPackageDialog";
import PfsUpgradeGate from "../components/pfs/PfsUpgradeGate";
import PfsSaveStatusChip from "../components/pfs/PfsSaveStatusChip";

const ASSET_FIELDS = [
  ["cash_on_hand", "Cash on hand & in banks"],
  ["savings", "Savings accounts"],
  ["retirement_accounts", "IRA / Retirement accounts"],
  ["accounts_receivable", "Accounts & notes receivable"],
  ["life_insurance_cash_value", "Life insurance — cash surrender value"],
  ["stocks_bonds", "Stocks & bonds"],
  ["automobile_value", "Automobile — present value"],
  ["other_personal_property", "Other personal property"],
  ["other_assets", "Other assets"],
];

const LIABILITY_FIELDS = [
  ["accounts_payable", "Accounts payable"],
  ["notes_payable", "Notes payable to banks"],
  ["auto_loans", "Auto installment loans"],
  ["other_installment_loans", "Other installment loans"],
  ["loan_on_life_insurance", "Loan on life insurance"],
  ["unpaid_taxes", "Unpaid taxes"],
  ["other_liabilities", "Other liabilities"],
];

const INCOME_FIELDS = [
  ["salary", "Salary / wages"],
  ["net_investment_income", "Net investment income"],
  ["real_estate_income", "Real estate income (rents)"],
  ["other_income", "Other income"],
];

const CONTINGENT_FIELDS = [
  ["contingent_endorser", "As endorser or co-maker"],
  ["contingent_legal", "Legal claims & judgments"],
  ["contingent_taxes", "Provision for federal income tax"],
  ["contingent_other", "Other contingent liabilities"],
];

/**
 * Stock symbol typeahead — debounced Alpha Vantage SYMBOL_SEARCH lookup with
 * a click-to-select dropdown. Used inside the Schedule of Stocks & Bonds.
 *
 *  - Free-text editing of `value` works exactly like a normal <input>.
 *  - 300ms debounce after typing → hits `/api/pfs/stock-search?q=...`.
 *  - Up to 10 hits rendered as a popover beneath the input.
 *  - `onSelect(match)` fires when the user clicks a result; caller decides
 *    what extra metadata (e.g. company name) to persist on the row.
 */
function StockSymbolInput({ value, onChange, onSelect, testid }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Close on outside click — keeps multiple typeahead inputs from
  // overlapping each other in the same schedule.
  useEffect(() => {
    function onDoc(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const search = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 1) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/pfs/stock-search", { params: { q } });
        setResults(data.results || []);
        setOpen((data.results || []).length > 0);
        setHighlight(-1);
      } catch {
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleChange = (e) => {
    const v = e.target.value.toUpperCase();
    onChange(v);
    search(v);
  };

  const pick = (match) => {
    onChange(match.symbol);
    onSelect?.(match);
    setOpen(false);
    setResults([]);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        className="input-clean w-full text-xs uppercase font-mono"
        value={value}
        onChange={handleChange}
        onFocus={() => results.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="AAPL or 'apple'"
        data-testid={testid}
        autoComplete="off"
      />
      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 bg-white border border-slate-300 rounded-sm shadow-lg max-h-72 overflow-y-auto min-w-[380px]"
          data-testid={`${testid}-dropdown`}
        >
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-400 italic">Searching…</div>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.symbol}-${i}`}
              type="button"
              onClick={() => pick(r)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-b-0 ${
                highlight === i ? "bg-violet-50" : "hover:bg-slate-50"
              }`}
              data-testid={`${testid}-option-${i}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold font-mono text-slate-900">{r.symbol}</span>
                <span className="text-[10px] font-mono text-slate-400">
                  {r.region}{r.currency ? ` · ${r.currency}` : ""}
                </span>
              </div>
              <div className="text-[11px] text-slate-600 truncate" title={r.name}>{r.name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


export default function PersonalFinancialStatement() {
  const { user } = useAuth();
  // PFS is included natively in Pro+/Enterprise; everyone else is here via
  // the $20/mo add-on (Starter + Loans Only). Add-on users don't get the
  // Lender Package bundle because the lender_package endpoints themselves
  // are still gated to Pro+ on the backend, and we'd rather hide it
  // entirely than render a button that 402s.
  const userPlan = (user?.subscription_plan || "").toLowerCase();
  const isAddonOnly = userPlan === "starter" || userPlan === "loans_only";
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [lenderPackageOpen, setLenderPackageOpen] = useState(false);
  const [shareLinkSnapshotForAudit, setShareLinkSnapshotForAudit] = useState(null);
  const [pfs, setPfs] = useState(null);
  // Auto-save state. `dirty` flips true on the first user edit and back
  // to false after a successful save. `lastSavedAt` is an epoch-ms used
  // to render "Saved Xs ago" in the header chip. `lastError` keeps the
  // chip honest if a background save fails.
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastError, setLastError] = useState(null);
  // Plan-gating: PFS is a Pro/Enterprise feature. We detect the 402 from
  // the load request and show a graceful upgrade screen instead of an
  // error toast (toast would feel like a bug to free-tier users).
  const [planGated, setPlanGated] = useState(false);
  // Stocks & Bonds — Alpha Vantage refresh state. Disables button + animates icon.
  const [refreshingPrices, setRefreshingPrices] = useState(false);

  const refreshStockPrices = async () => {
    setRefreshingPrices(true);
    try {
      const { data } = await api.post("/pfs/refresh-stock-prices");
      // Hydrate the rows from the response so cost_basis edits aren't lost.
      // Also refresh totals so the card subtotal/asset breakdown updates.
      setPfs((p) => ({
        ...p,
        stocks_bonds_schedule: data.rows || p.stocks_bonds_schedule,
      }));
      const failed = data.failed_count || 0;
      const updated = data.updated || 0;
      if (updated && !failed) {
        toast.success(`Refreshed ${updated} ${updated === 1 ? "holding" : "holdings"} from Alpha Vantage`);
      } else if (updated && failed) {
        toast.success(`Refreshed ${updated}; ${failed} could not be quoted (rate-limited or unknown symbol)`);
      } else if (!updated && failed) {
        toast.error("Could not fetch any quotes — Alpha Vantage may be rate-limited (free tier = 25/day).");
      } else {
        toast.message("Nothing to refresh — add a symbol + share count first.");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to refresh stock prices";
      toast.error(typeof msg === "string" ? msg : "Failed to refresh");
    } finally {
      setRefreshingPrices(false);
    }
  };

  // Edit a field through this wrapper so the auto-saver knows there are
  // pending changes to flush. Also used by every schedule helper below.
  const editPfs = (updater) => {
    setPfs(updater);
    setDirty(true);
  };

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/pfs");
      setPfs(r.data);
      setPlanGated(false);
      // Fresh server data — clear any pending dirty flag from a prior
      // session and reset the error chip.
      setDirty(false);
      setLastError(null);
    } catch (e) {
      if (e.response?.status === 402) {
        setPlanGated(true);
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load PFS");
      }
    } finally {
      setBusy(false);
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  // Iter66: when navigated here from the global Activity bell, auto-open
  // the share dialog with the matching link's audit log expanded.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const linkId = searchParams.get("openShareLink");
    const snapshotId = searchParams.get("openSnapshot");
    if (linkId) {
      setShareLinkSnapshotForAudit({ linkId, snapshotId });
      // Strip params so refresh doesn't re-trigger the dialog.
      const next = new URLSearchParams(searchParams);
      next.delete("openShareLink");
      next.delete("openSnapshot");
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (key, val) => editPfs((p) => ({ ...p, [key]: val }));
  const updateRow = (idx, key, val) =>
    editPfs((p) => ({
      ...p,
      real_estate_schedule: (p.real_estate_schedule || []).map((r, i) =>
        i === idx ? { ...r, [key]: val } : r,
      ),
    }));
  const addRow = () =>
    editPfs((p) => ({
      ...p,
      real_estate_schedule: [...(p.real_estate_schedule || []), {
        property_id: null, address: "", property_type: "", title_in_name_of: p.name || "",
        ownership_pct: 100, purchase_date: "", original_cost: 0, present_value: 0,
        mortgage_holder: "", mortgage_balance: 0, monthly_payment: 0, status: "Current",
      }],
    }));
  const removeRow = (idx) =>
    editPfs((p) => ({
      ...p,
      real_estate_schedule: (p.real_estate_schedule || []).filter((_, i) => i !== idx),
    }));

  const updateAuto = (idx, key, val) =>
    editPfs((p) => ({
      ...p,
      automobiles: (p.automobiles || []).map((a, i) => (i === idx ? { ...a, [key]: val } : a)),
    }));
  const addAuto = () =>
    editPfs((p) => ({
      ...p,
      automobiles: [...(p.automobiles || []), { year: "", make: "", model: "", present_value: 0 }],
    }));
  const removeAuto = (idx) =>
    editPfs((p) => ({
      ...p,
      automobiles: (p.automobiles || []).filter((_, i) => i !== idx),
    }));

  // Generic schedule helpers for stocks_bonds_schedule,
  // other_personal_property_schedule, other_assets_schedule.
  const updateList = (key, idx, field, val) =>
    editPfs((p) => ({
      ...p,
      [key]: (p[key] || []).map((r, i) => (i === idx ? { ...r, [field]: val } : r)),
    }));
  const addToList = (key, blank) =>
    editPfs((p) => ({ ...p, [key]: [...(p[key] || []), blank] }));
  const removeFromList = (key, idx) =>
    editPfs((p) => ({ ...p, [key]: (p[key] || []).filter((_, i) => i !== idx) }));

  // Live client-side totals — matches server-side _compute_totals exactly.
  const totals = useMemo(() => {
    if (!pfs) return null;
    const sched = pfs.real_estate_schedule || [];
    const reValue = sched.reduce((a, r) => a + num(r.present_value), 0);
    const reMort = sched.reduce((a, r) => a + num(r.mortgage_balance), 0);
    const autos = pfs.automobiles || [];
    const autosValue = autos.reduce((a, x) => a + num(x.present_value), 0);
    const stocks = pfs.stocks_bonds_schedule || [];
    const stocksValue = stocks.reduce((a, x) => a + num(x.present_value), 0);
    const opp = pfs.other_personal_property_schedule || [];
    const oppValue = opp.reduce((a, x) => a + num(x.present_value), 0);
    const oa = pfs.other_assets_schedule || [];
    const oaValue = oa.reduce((a, x) => a + num(x.present_value), 0);
    const np = pfs.notes_payable_schedule || [];
    const npBalance = np.reduce((a, x) => a + num(x.current_balance), 0);
    const al = pfs.auto_loans_schedule || [];
    const alBalance = al.reduce((a, x) => a + num(x.current_balance), 0);
    const oil = pfs.other_installment_loans_schedule || [];
    const oilBalance = oil.reduce((a, x) => a + num(x.current_balance), 0);
    const cash = pfs.cash_schedule || [];
    const cashScheduleTotal = cash.reduce((a, x) => a + num(x.amount), 0);

    // Schedule supersedes scalar when populated (matches server logic).
    const cashForTotal = cash.length ? cashScheduleTotal : num(pfs.cash_on_hand);
    const autoForTotal = autos.length ? autosValue : num(pfs.automobile_value);
    const stocksForTotal = stocks.length ? stocksValue : num(pfs.stocks_bonds);
    const oppForTotal = opp.length ? oppValue : num(pfs.other_personal_property);
    const oaForTotal = oa.length ? oaValue : num(pfs.other_assets);
    const npForTotal = np.length ? npBalance : num(pfs.notes_payable);
    const alForTotal = al.length ? alBalance : num(pfs.auto_loans);
    const oilForTotal = oil.length ? oilBalance : num(pfs.other_installment_loans);
    const REPLACED = new Set([
      "cash_on_hand", "automobile_value", "stocks_bonds", "other_personal_property", "other_assets",
    ]);
    const REPLACED_LIAB = new Set([
      "notes_payable", "auto_loans", "other_installment_loans",
    ]);
    const ta = ASSET_FIELDS
      .filter(([k]) => !REPLACED.has(k))
      .reduce((a, [k]) => a + num(pfs[k]), 0)
      + cashForTotal + autoForTotal + stocksForTotal + oppForTotal + oaForTotal + reValue;
    const tl = LIABILITY_FIELDS
      .filter(([k]) => !REPLACED_LIAB.has(k))
      .reduce((a, [k]) => a + num(pfs[k]), 0)
      + npForTotal + alForTotal + oilForTotal + reMort;
    const ti = INCOME_FIELDS.reduce((a, [k]) => a + num(pfs[k]), 0);
    const tc = CONTINGENT_FIELDS.reduce((a, [k]) => a + num(pfs[k]), 0);
    return {
      real_estate_value: reValue,
      real_estate_mortgage_balance: reMort,
      cash_schedule_total: cashScheduleTotal,
      automobiles_value: autosValue,
      stocks_bonds_value: stocksValue,
      other_personal_property_value: oppValue,
      other_assets_value: oaValue,
      notes_payable_balance: npBalance,
      auto_loans_balance: alBalance,
      other_installment_loans_balance: oilBalance,
      total_assets: ta,
      total_liabilities: tl,
      net_worth: ta - tl,
      total_annual_income: ti,
      total_contingent_liabilities: tc,
    };
  }, [pfs]);

  // Latest pfs ref so the debounced auto-save can read the freshest
  // state without restarting its timer on every keystroke.
  const pfsRef = useRef(pfs);
  useEffect(() => { pfsRef.current = pfs; }, [pfs]);

  const save = async (opts = {}) => {
    const { auto = false } = opts;
    const current = pfsRef.current;
    if (!current) return;
    setSaving(true);
    try {
      const body = { ...current };
      // Coerce numeric strings to numbers
      [...ASSET_FIELDS, ...LIABILITY_FIELDS, ...INCOME_FIELDS, ...CONTINGENT_FIELDS].forEach(([k]) => {
        body[k] = num(current[k]);
      });
      body.real_estate_schedule = (current.real_estate_schedule || []).map((r) => ({
        ...r,
        ownership_pct: num(r.ownership_pct),
        original_cost: num(r.original_cost),
        present_value: num(r.present_value),
        mortgage_balance: num(r.mortgage_balance),
        monthly_payment: num(r.monthly_payment),
      }));
      body.automobiles = (current.automobiles || []).map((a) => ({
        year: a.year === "" || a.year == null ? null : Number(a.year),
        make: String(a.make || ""),
        model: String(a.model || ""),
        present_value: num(a.present_value),
      }));
      body.stocks_bonds_schedule = (current.stocks_bonds_schedule || []).map((s) => ({
        symbol: String(s.symbol || "").toUpperCase(),
        shares: num(s.shares),
        cost_basis: num(s.cost_basis),
        present_value: num(s.present_value),
      }));
      body.other_personal_property_schedule = (current.other_personal_property_schedule || []).map((x) => ({
        description: String(x.description || ""),
        present_value: num(x.present_value),
      }));
      body.other_assets_schedule = (current.other_assets_schedule || []).map((x) => ({
        description: String(x.description || ""),
        present_value: num(x.present_value),
      }));
      body.cash_schedule = (current.cash_schedule || []).map((c) => ({
        institution: String(c.institution || ""),
        location: String(c.location || ""),
        amount: num(c.amount),
      }));
      const coerceLiability = (x) => ({
        lender: String(x.lender || ""),
        description: String(x.description || ""),
        original_amount: num(x.original_amount),
        current_balance: num(x.current_balance),
        monthly_payment: num(x.monthly_payment),
        maturity_date: x.maturity_date || null,
      });
      body.notes_payable_schedule = (current.notes_payable_schedule || []).map(coerceLiability);
      body.auto_loans_schedule = (current.auto_loans_schedule || []).map(coerceLiability);
      body.other_installment_loans_schedule = (current.other_installment_loans_schedule || []).map(coerceLiability);
      const r = await api.put("/pfs", body);
      // Use setPfs directly (NOT editPfs) so the post-save state doesn't
      // get re-marked dirty.
      setPfs(r.data);
      setDirty(false);
      setLastSavedAt(Date.now());
      setLastError(null);
      if (!auto) toast.success("PFS saved");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Save failed";
      setLastError(msg);
      if (!auto) toast.error(msg);
      // On auto-save failure we keep `dirty=true` so the next edit (or a
      // manual click) retries the save naturally.
    } finally {
      setSaving(false);
    }
  };

  // Debounced auto-save: 1.5s after the last edit, flush to the server.
  // Resets the timer on every dirty re-mark so a burst of typing only
  // produces one save at the end.
  useEffect(() => {
    if (!dirty || !pfs || saving) return;
    const id = setTimeout(() => { save({ auto: true }); }, 1500);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, pfs, saving]);

  const sync = async () => {
    if (!confirm("Refresh the Real Estate Schedule from your portfolio? Existing manual rows are preserved; properties already listed keep your overrides for title and ownership %.")) return;
    setSyncing(true);
    try {
      const r = await api.post("/pfs/sync-properties");
      setPfs(r.data);
      // Server persisted the sync — treat as a clean save so the chip
      // shows "Saved just now" instead of "Unsaved changes".
      setDirty(false);
      setLastSavedAt(Date.now());
      setLastError(null);
      toast.success(`Synced ${(r.data.real_estate_schedule || []).length} properties`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const downloadPdf = async () => {
    try {
      await authenticatedDownload(
        `/api/pfs/pdf`,
        `personal-financial-statement-${pfs?.as_of_date || "today"}.pdf`,
      );
    } catch {
      toast.error("PDF download failed");
    }
  };

  if (loaded && planGated) {
    return (
      <DashboardShell>
        <PfsUpgradeGate />
      </DashboardShell>
    );
  }

  if (!loaded || !pfs) {
    return (
      <DashboardShell>
        <div className="flex items-center justify-center py-24 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
    <div className="space-y-6" data-testid="pfs-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-slate-500">Lender package</div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Personal Financial Statement
          </h1>
          <p className="text-slate-600 text-sm mt-1 max-w-2xl">
            Editable banker-style PFS. Auto-calculates totals + net worth and pulls every property in your portfolio
            into the Real Estate Owned schedule. Download as a polished PDF or email it directly to your lender.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PfsNotificationsBell onOpenLink={(linkId) => setShareLinkSnapshotForAudit({ linkId })} />
          <PfsSaveStatusChip
            saving={saving}
            dirty={dirty}
            lastSavedAt={lastSavedAt}
            lastError={lastError}
          />
          <button onClick={() => save()} disabled={saving} className="btn-primary-dark inline-flex items-center gap-2 disabled:opacity-60" data-testid="pfs-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
          <button onClick={() => setHistoryOpen(true)} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-sm border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 transition-colors" data-testid="pfs-history-btn" title="Browse past versions of this PFS">
            <HistoryIcon className="w-4 h-4" /> History
          </button>
          <button onClick={downloadPdf} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-sm border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 transition-colors" data-testid="pfs-download-btn">
            <Download className="w-4 h-4" /> Download PDF
          </button>
          {!isAddonOnly && (
            <button onClick={() => setLenderPackageOpen(true)} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-sm border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 transition-colors" data-testid="pfs-lender-package-btn" title="One-click bundle: PFS + rent roll + lease binder + comp library">
              <Package className="w-4 h-4" /> Lender package
            </button>
          )}
          <button onClick={() => setEmailOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-sm inline-flex items-center gap-2" data-testid="pfs-email-btn">
            <Mail className="w-4 h-4" /> Email to lender
          </button>
        </div>
      </div>

      {/* KPI band */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI label="Total assets" value={totals.total_assets} testid="pfs-total-assets" bgClass="bg-green-50 border-green-200" />
        <KPI label="Total liabilities" value={totals.total_liabilities} testid="pfs-total-liabilities" bgClass="bg-red-50 border-red-200" />
        <KPI
          label="Net worth"
          value={totals.net_worth}
          tone={totals.net_worth >= 0 ? "emerald" : "rose"}
          testid="pfs-net-worth"
          bgClass="bg-green-50 border-green-200"
        />
      </div>

      {/* Personal info */}
      <Card title="Borrower" bgClass="bg-blue-50 border-blue-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Name" value={pfs.name || ""} onChange={(v) => updateField("name", v)} testid="pfs-name" />
          <Field label="Business" value={pfs.business_name || ""} onChange={(v) => updateField("business_name", v)} testid="pfs-business" />
          <Field label="Address" value={pfs.address || ""} onChange={(v) => updateField("address", v)} testid="pfs-address" />
          <label className="block">
            <div className="overline text-slate-500 mb-1">As of</div>
            <USDateInput
              value={pfs.as_of_date || ""}
              onChange={(v) => updateField("as_of_date", v)}
              testid="pfs-as-of"
            />
          </label>
        </div>
      </Card>

      {/* Assets ↔ Liabilities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Assets" total={totals.total_assets} totalTestId="pfs-assets-subtotal" bgClass="bg-green-50 border-green-200">
          <div className="space-y-2">
            {ASSET_FIELDS.filter(([k]) => k !== "automobile_value").map(([k, label]) => {
              // Hide scalar fields whose schedule has rows
              if (k === "cash_on_hand" && (pfs.cash_schedule || []).length > 0) return null;
              if (k === "stocks_bonds" && (pfs.stocks_bonds_schedule || []).length > 0) return null;
              if (k === "other_personal_property" && (pfs.other_personal_property_schedule || []).length > 0) return null;
              if (k === "other_assets" && (pfs.other_assets_schedule || []).length > 0) return null;
              return (
                <Row key={k} label={label}>
                  <MoneyInput
                    value={pfs[k]}
                    onChange={(v) => updateField(k, v)}
                    testid={`pfs-asset-${k}`}
                  />
                </Row>
              );
            })}
            {(pfs.cash_schedule || []).length > 0 && (
              <Row label="Cash on hand & in banks (from schedule below)" muted>
                <div className="text-right tabular-nums text-slate-700 pr-3" data-testid="pfs-cash-value-display">
                  {fmtUSD(totals.cash_schedule_total)}
                </div>
              </Row>
            )}
            {(pfs.stocks_bonds_schedule || []).length > 0 && (
              <Row label="Stocks & bonds (from schedule below)" muted>
                <div className="text-right tabular-nums text-slate-700 pr-3" data-testid="pfs-stocks-value-display">
                  {fmtUSD(totals.stocks_bonds_value)}
                </div>
              </Row>
            )}
            <Row label="Automobiles (from schedule below)" muted>
              <div className="text-right tabular-nums text-slate-700 pr-3" data-testid="pfs-autos-value-display">
                {fmtUSD(totals.automobiles_value || pfs.automobile_value || 0)}
              </div>
            </Row>
            {(pfs.other_personal_property_schedule || []).length > 0 && (
              <Row label="Other personal property (from schedule below)" muted>
                <div className="text-right tabular-nums text-slate-700 pr-3" data-testid="pfs-opp-value-display">
                  {fmtUSD(totals.other_personal_property_value)}
                </div>
              </Row>
            )}
            {(pfs.other_assets_schedule || []).length > 0 && (
              <Row label="Other assets (from schedule below)" muted>
                <div className="text-right tabular-nums text-slate-700 pr-3" data-testid="pfs-oa-value-display">
                  {fmtUSD(totals.other_assets_value)}
                </div>
              </Row>
            )}
            <Row label="Real estate (from schedule)" muted>
              <div className="text-right tabular-nums text-slate-700 pr-3" data-testid="pfs-re-value-display">
                {fmtUSD(totals.real_estate_value)}
              </div>
            </Row>
          </div>
        </Card>
        <Card title="Liabilities" total={totals.total_liabilities} totalTestId="pfs-liabilities-subtotal" bgClass="bg-red-50 border-red-200">
          <div className="space-y-2">
            {LIABILITY_FIELDS.map(([k, label]) => {
              if (k === "notes_payable" && (pfs.notes_payable_schedule || []).length > 0) return null;
              if (k === "auto_loans" && (pfs.auto_loans_schedule || []).length > 0) return null;
              if (k === "other_installment_loans" && (pfs.other_installment_loans_schedule || []).length > 0) return null;
              return (
                <Row key={k} label={label}>
                  <MoneyInput
                    value={pfs[k]}
                    onChange={(v) => updateField(k, v)}
                    testid={`pfs-liab-${k}`}
                  />
                </Row>
              );
            })}
            {(pfs.notes_payable_schedule || []).length > 0 && (
              <Row label="Notes payable to banks (from schedule below)" muted>
                <div className="text-right tabular-nums text-slate-700 pr-3" data-testid="pfs-np-balance-display">
                  {fmtUSD(totals.notes_payable_balance)}
                </div>
              </Row>
            )}
            {(pfs.auto_loans_schedule || []).length > 0 && (
              <Row label="Auto installment loans (from schedule below)" muted>
                <div className="text-right tabular-nums text-slate-700 pr-3" data-testid="pfs-al-balance-display">
                  {fmtUSD(totals.auto_loans_balance)}
                </div>
              </Row>
            )}
            {(pfs.other_installment_loans_schedule || []).length > 0 && (
              <Row label="Other installment loans (from schedule below)" muted>
                <div className="text-right tabular-nums text-slate-700 pr-3" data-testid="pfs-oil-balance-display">
                  {fmtUSD(totals.other_installment_loans_balance)}
                </div>
              </Row>
            )}
            <Row label="Mortgages on real estate (from schedule)" muted>
              <div className="text-right tabular-nums text-slate-700 pr-3" data-testid="pfs-re-mortgage-display">
                {fmtUSD(totals.real_estate_mortgage_balance)}
              </div>
            </Row>
          </div>
        </Card>
      </div>

      {/* Income + Contingent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Annual income" total={totals.total_annual_income} totalTestId="pfs-income-subtotal" bgClass="bg-amber-50 border-amber-200">
          <div className="space-y-2">
            {INCOME_FIELDS.map(([k, label]) => (
              <Row key={k} label={label}>
                <MoneyInput value={pfs[k]} onChange={(v) => updateField(k, v)} testid={`pfs-inc-${k}`} />
              </Row>
            ))}
          </div>
        </Card>
        <Card title="Contingent liabilities" total={totals.total_contingent_liabilities} totalTestId="pfs-contingent-subtotal" bgClass="bg-purple-50 border-purple-200">
          <div className="space-y-2">
            {CONTINGENT_FIELDS.map(([k, label]) => (
              <Row key={k} label={label}>
                <MoneyInput value={pfs[k]} onChange={(v) => updateField(k, v)} testid={`pfs-cont-${k}`} />
              </Row>
            ))}
          </div>
        </Card>
      </div>

      {/* Cash on hand & in banks Schedule */}
      <Card
        title="Schedule of cash on hand & in banks"
        total={totals.cash_schedule_total}
        totalTestId="pfs-cash-subtotal"
        right={
          <button
            onClick={() => addToList("cash_schedule", { institution: "", location: "", amount: 0 })}
            className="text-xs font-mono uppercase tracking-widest text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="pfs-add-cash-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add account
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pfs-cash-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-2">Financial institution</th>
                <th className="py-2 px-2">Location</th>
                <th className="py-2 px-2 text-right w-40">Amount</th>
                <th className="py-2 pl-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(pfs.cash_schedule || []).length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-slate-500 text-sm">
                  No cash accounts listed. Click <b>Add account</b> to enter each bank — institution / location / balance.
                </td></tr>
              )}
              {(pfs.cash_schedule || []).map((c, idx) => (
                <tr key={idx} className="border-b border-slate-100" data-testid={`pfs-cash-row-${idx}`}>
                  <td className="py-2 pr-2 align-top">
                    <input
                      className="input-clean w-full text-xs"
                      value={c.institution || ""}
                      onChange={(e) => updateList("cash_schedule", idx, "institution", e.target.value)}
                      placeholder="Chase Bank"
                      data-testid={`pfs-cash-${idx}-institution`}
                    />
                  </td>
                  <td className="py-2 px-2 align-top">
                    <input
                      className="input-clean w-full text-xs"
                      value={c.location || ""}
                      onChange={(e) => updateList("cash_schedule", idx, "location", e.target.value)}
                      placeholder="Austin, TX"
                      data-testid={`pfs-cash-${idx}-location`}
                    />
                  </td>
                  <td className="py-2 px-2 align-top">
                    <MoneyInput
                      value={c.amount}
                      onChange={(v) => updateList("cash_schedule", idx, "amount", v)}
                      testid={`pfs-cash-${idx}-amount`}
                    />
                  </td>
                  <td className="py-2 pl-2 align-top">
                    <button
                      onClick={() => removeFromList("cash_schedule", idx)}
                      className="text-slate-300 hover:text-red-600"
                      aria-label="Delete cash account"
                      data-testid={`pfs-cash-${idx}-delete`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {(pfs.cash_schedule || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-semibold">
                  <td colSpan={2} className="py-2 pr-2 text-right text-slate-700">Total</td>
                  <td className="py-2 px-2 text-right tabular-nums" data-testid="pfs-cash-table-total">
                    {fmtUSD(totals.cash_schedule_total)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Automobiles Schedule */}
      <Card
        title="Schedule of automobiles"
        total={totals.automobiles_value}
        totalTestId="pfs-autos-subtotal"
        right={
          <button onClick={addAuto} className="text-xs font-mono uppercase tracking-widest text-slate-700 hover:text-slate-900 inline-flex items-center gap-1" data-testid="pfs-add-auto-btn">
            <Plus className="w-3.5 h-3.5" /> Add automobile
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pfs-autos-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-2 w-20">Year</th>
                <th className="py-2 px-2">Make</th>
                <th className="py-2 px-2">Model</th>
                <th className="py-2 px-2 text-right">Present value</th>
                <th className="py-2 pl-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(pfs.automobiles || []).length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-slate-500 text-sm">
                  No automobiles. Click <b>Add automobile</b> to start a list — Year / Make / Model / Value.
                </td></tr>
              )}
              {(pfs.automobiles || []).map((a, idx) => (
                <tr key={idx} className="border-b border-slate-100" data-testid={`pfs-auto-row-${idx}`}>
                  <td className="py-2 pr-2 align-top">
                    <input type="number" min={1900} max={2100} step={1} className="input-clean w-full text-xs text-right" value={a.year ?? ""} onChange={(e) => updateAuto(idx, "year", e.target.value)} placeholder="2024" data-testid={`pfs-auto-${idx}-year`} />
                  </td>
                  <td className="py-2 px-2 align-top">
                    <input className="input-clean w-full text-xs" value={a.make || ""} onChange={(e) => updateAuto(idx, "make", e.target.value)} placeholder="Tesla" data-testid={`pfs-auto-${idx}-make`} />
                  </td>
                  <td className="py-2 px-2 align-top">
                    <input className="input-clean w-full text-xs" value={a.model || ""} onChange={(e) => updateAuto(idx, "model", e.target.value)} placeholder="Model Y Long Range" data-testid={`pfs-auto-${idx}-model`} />
                  </td>
                  <td className="py-2 px-2 align-top w-32">
                    <MoneyInput value={a.present_value} onChange={(v) => updateAuto(idx, "present_value", v)} testid={`pfs-auto-${idx}-value`} />
                  </td>
                  <td className="py-2 pl-2 align-top">
                    <button onClick={() => removeAuto(idx)} className="text-slate-300 hover:text-red-600" aria-label="Delete automobile" data-testid={`pfs-auto-${idx}-delete`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {(pfs.automobiles || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-semibold">
                  <td colSpan={3} className="py-2 pr-2 text-right text-slate-700">Total</td>
                  <td className="py-2 px-2 text-right tabular-nums" data-testid="pfs-autos-total">{fmtUSD(totals.automobiles_value)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Stocks & Bonds Schedule */}
      <Card
        title="Schedule of stocks & bonds"
        total={totals.stocks_bonds_value}
        totalTestId="pfs-stocks-subtotal"
        right={
          <div className="flex items-center gap-3">
            <button
              onClick={refreshStockPrices}
              disabled={refreshingPrices}
              className="text-xs font-mono uppercase tracking-widest text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="pfs-refresh-stock-prices-btn"
              title="Auto-update each holding's Present Value using live Alpha Vantage quotes (price × shares)."
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshingPrices ? "animate-spin" : ""}`} />
              {refreshingPrices ? "Refreshing…" : "Refresh prices"}
            </button>
            <button onClick={() => addToList("stocks_bonds_schedule", { symbol: "", shares: 0, cost_basis: 0, present_value: 0 })} className="text-xs font-mono uppercase tracking-widest text-slate-700 hover:text-slate-900 inline-flex items-center gap-1" data-testid="pfs-add-stock-btn">
              <Plus className="w-3.5 h-3.5" /> Add holding
            </button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pfs-stocks-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-2 w-56">Symbol</th>
                <th className="py-2 px-2 text-right w-24">Shares</th>
                <th className="py-2 px-2 text-right w-32">Cost basis</th>
                <th className="py-2 px-2 text-right w-32">Present value</th>
                <th className="py-2 pl-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(pfs.stocks_bonds_schedule || []).length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-slate-500 text-sm">
                  No holdings. Click <b>Add holding</b> for a brokerage-style breakdown — Symbol / Shares / Cost basis / Present value.
                </td></tr>
              )}
              {(pfs.stocks_bonds_schedule || []).map((s, idx) => (
                <tr key={idx} className="border-b border-slate-100" data-testid={`pfs-stock-row-${idx}`}>
                  <td className="py-2 pr-2 align-top">
                    <StockSymbolInput
                      value={s.symbol || ""}
                      onChange={(v) => updateList("stocks_bonds_schedule", idx, "symbol", v)}
                      onSelect={(match) => {
                        // When the user picks a search hit, lock the canonical symbol +
                        // stash the company name so it shows under the row. The price
                        // refresh button is the source of truth for present_value.
                        editPfs((p) => ({
                          ...p,
                          stocks_bonds_schedule: (p.stocks_bonds_schedule || []).map((row, i) =>
                            i === idx ? { ...row, symbol: match.symbol, security_name: match.name } : row
                          ),
                        }));
                      }}
                      testid={`pfs-stock-${idx}-symbol`}
                    />
                    {s.security_name && (
                      <div className="text-[10px] font-mono text-slate-500 mt-0.5 truncate" title={s.security_name}>
                        {s.security_name}
                      </div>
                    )}
                    {s.last_quoted_at && (() => {
                      const ts = new Date(s.last_quoted_at).getTime();
                      const ageMin = (Date.now() - ts) / 60000;
                      // "Live" = quote freshness within the cache TTL (~15 min).
                      // Beyond that → "Stale" so the user knows a snapshot-time
                      // refresh is warranted before emailing a lender.
                      const live = ageMin <= 15;
                      let label;
                      if (ageMin < 1) label = "just now";
                      else if (ageMin < 60) label = `${Math.floor(ageMin)} min ago`;
                      else if (ageMin < 60 * 24) label = `${Math.floor(ageMin / 60)} hr ago`;
                      else label = `${Math.floor(ageMin / (60 * 24))} d ago`;
                      return (
                        <div className="mt-1 flex items-center gap-1.5" data-testid={`pfs-stock-${idx}-pill`}>
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-widest border ${
                              live
                                ? "bg-green-50 text-green-800 border-green-200"
                                : "bg-red-50 text-red-800 border-red-200"
                            }`}
                            title={`Quoted at ${new Date(s.last_quoted_at).toLocaleString()}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-green-600" : "bg-red-600"}`} />
                            {live ? "Live" : "Stale"}
                          </span>
                          <span className="text-[10px] font-mono text-slate-500">· {label}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="py-2 px-2 align-top">
                    <input type="number" step="any" className="input-clean w-full text-xs text-right" value={s.shares ?? ""} onChange={(e) => updateList("stocks_bonds_schedule", idx, "shares", e.target.value)} placeholder="0" data-testid={`pfs-stock-${idx}-shares`} />
                  </td>
                  <td className="py-2 px-2 align-top">
                    <MoneyInput value={s.cost_basis} onChange={(v) => updateList("stocks_bonds_schedule", idx, "cost_basis", v)} testid={`pfs-stock-${idx}-basis`} />
                  </td>
                  <td className="py-2 px-2 align-top">
                    <MoneyInput value={s.present_value} onChange={(v) => updateList("stocks_bonds_schedule", idx, "present_value", v)} testid={`pfs-stock-${idx}-value`} />
                  </td>
                  <td className="py-2 pl-2 align-top">
                    <button onClick={() => removeFromList("stocks_bonds_schedule", idx)} className="text-slate-300 hover:text-red-600" aria-label="Delete holding" data-testid={`pfs-stock-${idx}-delete`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {(pfs.stocks_bonds_schedule || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-semibold">
                  <td colSpan={3} className="py-2 pr-2 text-right text-slate-700">Total</td>
                  <td className="py-2 px-2 text-right tabular-nums" data-testid="pfs-stocks-total">{fmtUSD(totals.stocks_bonds_value)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Other Personal Property Schedule */}
      <DescribedScheduleCard
        title="Schedule of other personal property"
        rows={pfs.other_personal_property_schedule || []}
        total={totals.other_personal_property_value}
        testidPrefix="pfs-opp"
        addLabel="Add item"
        emptyHint="Add jewelry, art, collectibles, household goods — anything personal that has resale value."
        onAdd={() => addToList("other_personal_property_schedule", { description: "", present_value: 0 })}
        onUpdate={(idx, field, val) => updateList("other_personal_property_schedule", idx, field, val)}
        onRemove={(idx) => removeFromList("other_personal_property_schedule", idx)}
      />

      {/* Other Assets Schedule */}
      <DescribedScheduleCard
        title="Schedule of other assets"
        rows={pfs.other_assets_schedule || []}
        total={totals.other_assets_value}
        testidPrefix="pfs-oa"
        addLabel="Add asset"
        emptyHint="Private equity, LP/LLC interests, business goodwill, crypto, anything not covered above."
        onAdd={() => addToList("other_assets_schedule", { description: "", present_value: 0 })}
        onUpdate={(idx, field, val) => updateList("other_assets_schedule", idx, field, val)}
        onRemove={(idx) => removeFromList("other_assets_schedule", idx)}
      />

      {/* Notes Payable Schedule */}
      <LiabilityScheduleCard
        title="Schedule of notes payable to banks"
        rows={pfs.notes_payable_schedule || []}
        total={totals.notes_payable_balance}
        testidPrefix="pfs-np"
        addLabel="Add note"
        showDescription={false}
        emptyHint="Per-creditor notes payable to banks. Each row holds the lender, original amount, current balance, monthly payment, and maturity date."
        onAdd={() => addToList("notes_payable_schedule", { lender: "", description: "", original_amount: 0, current_balance: 0, monthly_payment: 0, maturity_date: "" })}
        onUpdate={(idx, field, val) => updateList("notes_payable_schedule", idx, field, val)}
        onRemove={(idx) => removeFromList("notes_payable_schedule", idx)}
      />

      {/* Auto Loans Schedule */}
      <LiabilityScheduleCard
        title="Schedule of auto installment loans"
        rows={pfs.auto_loans_schedule || []}
        total={totals.auto_loans_balance}
        testidPrefix="pfs-al"
        addLabel="Add auto loan"
        showDescription={true}
        descriptionLabel="Vehicle (Year Make Model)"
        emptyHint="Per-vehicle auto installment loans — lender, vehicle, original amount, current balance, monthly payment, and maturity."
        onAdd={() => addToList("auto_loans_schedule", { lender: "", description: "", original_amount: 0, current_balance: 0, monthly_payment: 0, maturity_date: "" })}
        onUpdate={(idx, field, val) => updateList("auto_loans_schedule", idx, field, val)}
        onRemove={(idx) => removeFromList("auto_loans_schedule", idx)}
      />

      {/* Other Installment Loans Schedule */}
      <LiabilityScheduleCard
        title="Schedule of other installment loans"
        rows={pfs.other_installment_loans_schedule || []}
        total={totals.other_installment_loans_balance}
        testidPrefix="pfs-oil"
        addLabel="Add loan"
        showDescription={true}
        descriptionLabel="Description"
        emptyHint="Student loans, equipment loans, signature loans, BNPL — anything else with a fixed payment schedule."
        onAdd={() => addToList("other_installment_loans_schedule", { lender: "", description: "", original_amount: 0, current_balance: 0, monthly_payment: 0, maturity_date: "" })}
        onUpdate={(idx, field, val) => updateList("other_installment_loans_schedule", idx, field, val)}
        onRemove={(idx) => removeFromList("other_installment_loans_schedule", idx)}
      />

      {/* Real Estate Schedule */}
      <Card
        title="Schedule of real estate owned"
        right={
          <div className="flex items-center gap-2">
            <button onClick={sync} disabled={syncing} className="text-xs font-mono uppercase tracking-widest text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-60" data-testid="pfs-sync-btn">
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Pull from portfolio
            </button>
            <button onClick={addRow} className="text-xs font-mono uppercase tracking-widest text-slate-700 hover:text-slate-900 inline-flex items-center gap-1" data-testid="pfs-add-row-btn">
              <Plus className="w-3.5 h-3.5" /> Add row
            </button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pfs-schedule-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-2">Address / Type</th>
                <th className="py-2 px-2">Title in</th>
                <th className="py-2 px-2 text-right">% Own</th>
                <th className="py-2 px-2">Acquired</th>
                <th className="py-2 px-2 text-right">Cost</th>
                <th className="py-2 px-2 text-right">Present value</th>
                <th className="py-2 px-2">Mortgage holder</th>
                <th className="py-2 px-2 text-right">Balance</th>
                <th className="py-2 px-2 text-right">Pmt/mo</th>
                <th className="py-2 px-2">Status</th>
                <th className="py-2 pl-2"></th>
              </tr>
            </thead>
            <tbody>
              {(pfs.real_estate_schedule || []).length === 0 && (
                <tr><td colSpan={11} className="py-6 text-center text-slate-500 text-sm">
                  No properties on the schedule. Click <b>Pull from portfolio</b> to auto-populate from your live properties.
                </td></tr>
              )}
              {(pfs.real_estate_schedule || []).map((r, idx) => (
                <tr key={r.property_id || `manual-${idx}`} className="border-b border-slate-100" data-testid={`pfs-row-${idx}`}>
                  <td className="py-2 pr-2 align-top min-w-[200px]">
                    <input className="input-clean w-full text-xs" placeholder="Address" value={r.address || ""} onChange={(e) => updateRow(idx, "address", e.target.value)} data-testid={`pfs-row-${idx}-address`} />
                    <input className="input-clean w-full text-xs mt-1 text-slate-500" placeholder="Type" value={r.property_type || ""} onChange={(e) => updateRow(idx, "property_type", e.target.value)} />
                  </td>
                  <td className="py-2 px-2 align-top min-w-[120px]">
                    <input className="input-clean w-full text-xs" value={r.title_in_name_of || ""} onChange={(e) => updateRow(idx, "title_in_name_of", e.target.value)} />
                  </td>
                  <td className="py-2 px-2 align-top w-20">
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        className="input-clean w-full text-xs text-right pr-5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        value={r.ownership_pct ?? ""}
                        onChange={(e) => updateRow(idx, "ownership_pct", e.target.value)}
                        data-testid={`pfs-row-${idx}-ownership-pct`}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-mono pointer-events-none select-none">%</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 align-top min-w-[110px]">
                    <USDateInput value={r.purchase_date || ""} onChange={(v) => updateRow(idx, "purchase_date", v)} testid={`pfs-row-${idx}-acquired`} />
                  </td>
                  <td className="py-2 px-2 align-top w-40">
                    <MoneyInput value={r.original_cost} onChange={(v) => updateRow(idx, "original_cost", v)} testid={`pfs-row-${idx}-cost`} />
                  </td>
                  <td className="py-2 px-2 align-top w-40">
                    <MoneyInput value={r.present_value} onChange={(v) => updateRow(idx, "present_value", v)} testid={`pfs-row-${idx}-pv`} />
                  </td>
                  <td className="py-2 px-2 align-top min-w-[120px]">
                    <input className="input-clean w-full text-xs" value={r.mortgage_holder || ""} onChange={(e) => updateRow(idx, "mortgage_holder", e.target.value)} />
                  </td>
                  <td className="py-2 px-2 align-top w-40">
                    <MoneyInput value={r.mortgage_balance} onChange={(v) => updateRow(idx, "mortgage_balance", v)} testid={`pfs-row-${idx}-bal`} />
                  </td>
                  <td className="py-2 px-2 align-top w-36">
                    <MoneyInput value={r.monthly_payment} onChange={(v) => updateRow(idx, "monthly_payment", v)} />
                  </td>
                  <td className="py-2 px-2 align-top min-w-[100px]">
                    <input className="input-clean w-full text-xs" value={r.status || ""} onChange={(e) => updateRow(idx, "status", e.target.value)} />
                  </td>
                  <td className="py-2 pl-2 align-top">
                    <button onClick={() => removeRow(idx)} className="text-slate-300 hover:text-red-600" aria-label="Delete row" data-testid={`pfs-row-${idx}-delete`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {(pfs.real_estate_schedule || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-semibold">
                  <td colSpan={5} className="py-2 pr-2 text-right text-slate-700">Totals</td>
                  <td className="py-2 px-2 text-right tabular-nums" data-testid="pfs-schedule-pv-total">{fmtUSD(totals.real_estate_value)}</td>
                  <td></td>
                  <td className="py-2 px-2 text-right tabular-nums" data-testid="pfs-schedule-bal-total">{fmtUSD(totals.real_estate_mortgage_balance)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Notes */}
      <Card title="Notes">
        <textarea
          className="input-clean w-full min-h-[80px]"
          value={pfs.notes || ""}
          onChange={(e) => updateField("notes", e.target.value)}
          placeholder="Optional context — explain any contingent liabilities, recent changes in income, etc."
          maxLength={4000}
          data-testid="pfs-notes"
        />
      </Card>

      <EmailToLenderDialog open={emailOpen} onOpenChange={setEmailOpen} pfs={pfs} totals={totals} />
      <LenderPackageDialog open={lenderPackageOpen} onOpenChange={setLenderPackageOpen} />
      <PfsHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} onRestored={load} />
      <PfsShareDialog
        open={!!shareLinkSnapshotForAudit}
        onOpenChange={(o) => { if (!o) setShareLinkSnapshotForAudit(null); }}
        snapshotId={shareLinkSnapshotForAudit?.snapshotId || null}
        focusLinkId={shareLinkSnapshotForAudit?.linkId || null}
        snapshotLabel="Lender activity"
      />
    </div>
    </DashboardShell>
  );
}
